import React, { useRef, useState, useEffect, useMemo } from "react";
import images from "../../../assets/images";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "../../../styles/ag-grid-custom.css";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import { ModuleRegistry } from "ag-grid-community";
import { CellStyleModule, PaginationModule } from "ag-grid-community";
import { Search } from "lucide-react";
import ActionModal from "./ActionModal";
import CreateIncident from "./CreateIncidentModal";
import { toast } from "react-toastify";
import { usePremisesStore, useRegionStore } from "../../../store/store";
import { generateIncidentExcel, formatDate } from "../utils/utils";
import { usePermissions } from "../../../hooks/usePermission";
import { Dropdown } from "../../../components/dropdown/Dropdown";
import { Incident, ApiResponse } from "../types/types";
import {
  calculateIncidentStats,
  fetchIncidentActionsWithQuery,
  fetchRCAData,
} from "../services/service";
import { INCIDENT_STATUS_COLORS } from "../../../constants/incidents";
import {
  useDepartmentIncidentsQuery,
  useInvalidateIncidents,
} from "../hooks/useIncidentQueries";

ModuleRegistry.registerModules([CellStyleModule, PaginationModule]);

// ============================================================================
// TYPES
// ============================================================================

export interface IncidentDashboardConfig {
  /** Department name string passed to the fetch service and CreateIncident modal */
  department: string;
  /** Page title displayed at the top */
  pageTitle: string;
  /** Module key for RBAC permission checks */
  moduleKey: string;
  /** Service function to fetch incidents for this department */
  fetchService: (department: string) => Promise<ApiResponse<Incident[]>>;
}

// ============================================================================
// COMPONENT
// ============================================================================

const IncidentDashboard: React.FC<IncidentDashboardConfig> = ({
  department,
  pageTitle,
  moduleKey,
  fetchService,
}) => {
  // ---------------------------------------------------------------------------
  // STATE
  // ---------------------------------------------------------------------------
  const [isOpen, setIsOpen] = useState(false);
  const [isRCA, setIsRCA] = useState(false);
  const [isDownload, setIsDownload] = useState(false);
  const [isIncidentModal, setIsIncidentModal] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<Incident | null>(null);
  const [mode, setMode] = useState<"create" | "view" | "update">("create");
  const [actionId, setActionId] = useState<string | undefined>(undefined);
  const [rcaId, setRcaId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPremises, setSelectedPremises] = useState<string>("");
  const [selectedRegion, setSelectedRegion] = useState<string>("");

  const handleRegionChange = (value: string) => {
    setSelectedRegion(value);
    if (!value || value === "all") {
      setSelectedPremises("");
    }
  };
  const handlePremisesChange = (value: string) => {
    setSelectedPremises(value);
    if (!value || value === "all") {
      setSelectedRegion("");
    }
  };

  const gridRef = useRef<AgGridReact>(null);
  const { fetchPremises } = usePremisesStore();
  const { fetchRegions } = useRegionStore();
  const { canRead, canCreate, canUpdate } = usePermissions();

  const invalidateIncidents = useInvalidateIncidents();

  const { data: rowData = [], isLoading: loading } = useDepartmentIncidentsQuery(
    department,
    fetchService
  );

  // Data fetching is handled by useDepartmentIncidentsQuery hook above.

  // ---------------------------------------------------------------------------
  // HANDLERS
  // ---------------------------------------------------------------------------

  useEffect(() => {
    fetchPremises();
    fetchRegions();
  }, [fetchPremises, fetchRegions]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const filteredData = rowData.filter((incident: Incident) => {
    const matchesSearch = incident.incidentNumber
      ?.toLowerCase()
      .includes(searchTerm.toLowerCase());
    const matchesPremises = selectedPremises
      ? incident.hospitalName === selectedPremises
      : true;
    const matchesRegion = selectedRegion
      ? incident.region === selectedRegion
      : true;
    return matchesSearch && matchesPremises && matchesRegion;
  });

  const stats = useMemo(() => calculateIncidentStats(filteredData), [filteredData]);

  const handleViewIncident = (data: Incident) => {
    setSelectedRowData({
      ...data,
      reportDate: data.reportDate,
    });
    setActionId(data.hasActions && data.actionId ? data.actionId : undefined);
    setRcaId(data.hasRca && data.rcaId ? data.rcaId : null);
    setMode("view");

    if (data.status === "Open") {
      setIsIncidentModal(true);
      setIsOpen(false);
      setIsRCA(false);
    } else if (data.status === "Pending") {
      setIsOpen(true);
      setIsRCA(false);
      setIsIncidentModal(false);
    } else if (data.status === "Resolved") {
      setIsRCA(true);
      setIsOpen(false);
      setIsIncidentModal(false);
    }
  };

  const handleEditIncident = (data: Incident) => {
    if (data.status !== "Open" || data.hasActions) {
      toast.warn("Cannot edit incident: Status is not Open or actions exist");
      return;
    }
    if (!data.id) {
      toast.error("Invalid incident ID");
      return;
    }
    setSelectedRowData({
      ...data,
      reportDate: data.reportDate,
    });
    setMode("update");
    setIsIncidentModal(true);
    setIsOpen(false);
    setIsRCA(false);
  };

  const handleDownloadIncident = async (data: Incident) => {
    try {
      let sendModalData = null;
      let rcaData = null;

      if (
        data.status.toLowerCase() === "pending" ||
        data.status.toLowerCase() === "resolved"
      ) {
        try {
          const sendResponse = await fetchIncidentActionsWithQuery({
            incident: data.id,
          });
          sendModalData = sendResponse.data?.[0] || null;
        } catch (actionError) {
          console.warn("No action data found for incident:", data.id);
        }
      }

      if (data.status.toLowerCase() === "resolved") {
        try {
          const rcaResponse = await fetchRCAData({ incident: data.id });
          rcaData = rcaResponse.data?.[0] || null;
        } catch (rcaError) {
          console.warn("No RCA data found for incident:", data.id);
        }
      }

      generateIncidentExcel(data, sendModalData as any, rcaData as any);
    } catch (error) {
      console.error("Error preparing Excel download:", error);
      toast.error("Failed to generate Excel file");
    }
  };

  const handleActionComplete = () => {
    setIsOpen(false);
    setIsRCA(false);
    setIsDownload(false);
    setIsIncidentModal(false);
    setSelectedRowData(null);
    setMode("create");
    setActionId(undefined);
    setRcaId(null);
    invalidateIncidents();
  };

  // ---------------------------------------------------------------------------
  // AG-GRID COLUMN DEFINITIONS
  // ---------------------------------------------------------------------------

  const columnDefs: ColDef<Incident>[] = [
    {
      headerName: "Incident Number",
      field: "incidentNumber",
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff" },
      cellStyle: { padding: "10px 0 0 5px" },
    },
    {
      headerName: "Report Date",
      field: "reportDate",
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff" },
      cellStyle: { padding: "10px 0 0 0" },
      valueFormatter: (params) => formatDate(params.value),
    },
    {
      headerName: "Department",
      field: "department",
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff" },
      cellStyle: { padding: "10px 0 0 0" },
    },
    {
      headerName: "Incident Date",
      field: "incidentDate",
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff" },
      cellStyle: { padding: "10px 0 0 0" },
      valueFormatter: (params) => formatDate(params.value),
    },
    {
      headerName: "Days Open",
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff" },
      cellStyle: { padding: "10px 0 0 0" },
      valueGetter: (params) => {
        if (!params.data) return "—";
        const startDateStr = params.data.reportDate || params.data.incidentDate;
        if (!startDateStr) return "—";

        const start = new Date(startDateStr);
        start.setHours(0, 0, 0, 0);

        const end = params.data.status === "Resolved" && params.data.closureDate
          ? new Date(params.data.closureDate)
          : new Date();
        end.setHours(0, 0, 0, 0);

        const diffTime = end.getTime() - start.getTime();
        const diffDays = Math.max(0, Math.round(diffTime / (1000 * 60 * 60 * 24)));
        return `${diffDays} Days`;
      },
    },
    {
      headerName: "Reported By",
      field: "reportedBy",
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff" },
      cellStyle: { padding: "10px 0 0 0" },
    },
    {
      headerName: "Corrective Action",
      field: "implementedCorrectiveActions",
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff" },
      cellStyle: { padding: "10px 0 0 0" },
      valueFormatter: (params) => params.value ? "Completed" : "Pending",
    },
    {
      headerName: "Incident Status",
      field: "status",
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff" },
      cellStyle: (params) => {
        const color = INCIDENT_STATUS_COLORS[params.value] || "black";
        return {
          color,
          fontWeight: "bold",
          padding: "10px 0 0 0",
          border: "1px solid black",
        };
      },
    },
    {
      headerName: "RCA Status",
      field: "rcaStatus",
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff" },
      cellStyle: (params) => {
        let color = "#999";
        if (params.value === "Completed") color = "#4CAF50";
        else if (params.value === "In Progress") color = "#FF9800";
        else if (params.value === "Draft") color = "#2196F3";
        return { color, fontWeight: "600", padding: "10px 0 0 0" };
      },
      valueFormatter: (params) => {
        if (!params.data) return "—";
        if (params.data.status === "Open") return "N/A";
        if (params.value === "Completed") return "Completed";
        if (params.value === "In Progress") return "In Progress";
        if (params.value === "Draft") return "Draft";
        return "Not Started";
      },
    },
    {
      headerName: "Action",
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff", textAlign: "center" },
      cellStyle: { padding: "10px 0 0 0" },
      cellRenderer: (params: ICellRendererParams<Incident>) => {
        const data = params.data;
        if (!data) return null;
        const isUpdateDisabled =
          data.status !== "Open" || data.hasActions;
        return (
          <div className="flex items-center gap-2">
            {canRead(moduleKey) && (
              <button onClick={() => handleViewIncident(data)}>
                <img className="iconTd" src={images.viewIcon} alt="View" />
              </button>
            )}
            {canUpdate(moduleKey) && (
              <button
                onClick={() => handleEditIncident(data)}
                disabled={isUpdateDisabled}
                className={
                  isUpdateDisabled ? "opacity-50 cursor-not-allowed" : ""
                }
              >
                <img className="iconTd" src={images.editIcon} alt="Edit" />
              </button>
            )}
            {canRead(moduleKey) && (
              <button onClick={() => handleDownloadIncident(data)}>
                <img
                  className="iconTd"
                  src={images.downloadIcon}
                  alt="Download"
                />
              </button>
            )}
          </div>
        );
      },
    },
  ];

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------

  return (
    <div className="px-2 py-4 mt-14 w-full h-full">
      <h1 className="text-2xl font-semibold text-[#61748e] mb-4">
        {pageTitle}
      </h1>
      <span className="flex justify-between gap-4">
        <span className="flex items-center gap-4">
          <Dropdown
            selectedValue={selectedRegion}
            onChange={handleRegionChange}
            type="region"
          />
          <Dropdown
            selectedValue={selectedPremises}
            onChange={handlePremisesChange}
            type="premises"
            selectedRegion={selectedRegion}
          />
        </span>
        {canCreate(moduleKey) && (
          <span className="flex items-center">
            {/* {canRead(moduleKey) && (
              <button
                className="flex items-center justify-center font-semibold gap-3 bg-[#4f5762] text-white px-4 py-2 rounded-full mr-4"
                onClick={() => {
                  setSelectedRowData(null); 
                  setIsDownload(true);
                }}
              >
                <img src={images.downloadIcon} alt="" className="w-5 h-5 text-white" />
                BULK EXPORT
              </button>
            )} */}
            <button
              className="flex items-center justify-center font-semibold gap-3 bg-[#0fa9a6] text-white px-4 py-2 rounded-full"
              onClick={() => {
                setIsOpen(false);
                setMode("create");
                setSelectedRowData(null);
                setIsRCA(false);
                setIsIncidentModal(true);
              }}
            >
              <img src={images.lawIcon} alt="" className="w-6 h-6 text-white" />
              CREATE INCIDENT
            </button>
          </span>
        )}
      </span>

      <div className="cardNum mt-4 mb-4">
        <div className="row">
          <div className="col-sm-3">
            <div className="card">
              <div className="cardIconCont">
                <span className="font-medium text-md">Total Incidents</span>
                <h3>{stats.totalIncident}</h3>
              </div>
              <div className="cardIcon cardIconColor1">
                <img className="iconTd" src={images.premisesIcon} alt="" />
              </div>
            </div>
          </div>
          <div className="col-sm-3">
            <div className="card">
              <div className="cardIconCont">
                <span className="font-medium text-md">Pending Incidents</span>
                <h3>{stats.pending}</h3>
              </div>
              <div className="cardIcon cardIconColor2">
                <img className="iconTd" src={images.premisesIcon} alt="" />
              </div>
            </div>
          </div>
          <div className="col-sm-3">
            <div className="card">
              <div className="cardIconCont">
                <span className="font-medium text-md">Resolved Incidents</span>
                <h3>
                  {stats.resolved} <span>({stats.totalResolvedPercentage}%)</span>
                </h3>
              </div>
              <div className="cardIcon cardIconColor3">
                <img className="iconTd" src={images.premisesIcon} alt="" />
              </div>
            </div>
          </div>
          <div className="col-sm-3">
            <div className="card">
              <div className="cardIconCont">
                <span className="font-medium text-md">Notifications (RCA)</span>
                <h3>{stats.notification}</h3>
              </div>
              <div className="cardIcon cardIconColor4">
                <img className="iconTd" src={images.premisesIcon} alt="" />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-[#ffffff] w-[100%]">
        <div className="px-3 py-3">
          <div className="relative flex justify-between items-center">
            <div className="flex w-10/12 justify-start items-center">
              <Search className="absolute left-3 text-gray-500" />
              <input
                type="text"
                placeholder="Search by Incident Number"
                className="border-2 border-[#4f5762] mr-5 w-[300px] px-10 py-2 rounded placeholder:font-semibold placeholder:text-[#4e545d]"
                value={searchTerm}
                onChange={handleSearch}
              />
            </div>
          </div>
        </div>
        <div className="ag-theme-alpine mt-4">
          {loading ? (
            <div className="text-center py-4">Loading incidents...</div>
          ) : (
            <AgGridReact
              ref={gridRef}
              rowData={filteredData}
              columnDefs={columnDefs}
              rowHeight={50}
              headerHeight={50}
              paginationPageSize={5}
              pagination={true}
              domLayout="autoHeight"
              defaultColDef={{
                sortable: true,
                resizable: true,
              }}
              getRowStyle={() => ({
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                marginBottom: "8px",
                borderRadius: "4px",
                backgroundColor: "white",
              })}
            />
          )}
        </div>
      </div>

      {isOpen && (
        <ActionModal
          showModal={isOpen}
          showRCAModal={false}
          showDownload={false}
          setShowModal={setIsOpen}
          setShowRCAModal={setIsRCA}
          setShowDownload={setIsDownload}
          data={selectedRowData}
          setSelectedRowData={setSelectedRowData}
          onActionComplete={handleActionComplete}
          mode={mode}
          actionId={actionId}
          rcaId={rcaId}
          setRcaId={setRcaId}
          moduleKey={moduleKey}
        />
      )}

      {isRCA && (
        <ActionModal
          showModal={false}
          showRCAModal={isRCA}
          showDownload={false}
          setShowModal={setIsOpen}
          setShowRCAModal={setIsRCA}
          setShowDownload={setIsDownload}
          data={selectedRowData}
          setSelectedRowData={setSelectedRowData}
          onActionComplete={handleActionComplete}
          mode={mode}
          actionId={actionId}
          setRcaId={setRcaId}
          rcaId={rcaId || null}
          moduleKey={moduleKey}
        />
      )}

      {isDownload && (
        <ActionModal
          showModal={false}
          showRCAModal={false}
          showDownload={isDownload}
          setShowModal={setIsOpen}
          setShowRCAModal={setIsRCA}
          setShowDownload={setIsDownload}
          data={selectedRowData}
          setSelectedRowData={setSelectedRowData}
          onActionComplete={handleActionComplete}
          mode={mode}
          actionId={actionId}
          setRcaId={setRcaId}
          rcaId={rcaId || null}
          moduleKey={moduleKey}
          department={department}
        />
      )}

      {isIncidentModal && (
        <CreateIncident
          showModal={isIncidentModal}
          setShowModal={setIsIncidentModal}
          departmentName={department}
          onIncidentCreated={handleActionComplete}
          mode={mode}
          incidentId={selectedRowData?.id}
          moduleKey={moduleKey}
        />
      )}
    </div>
  );
};

export default IncidentDashboard;
